import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchMenuEnhancementRequest } from '../../redux/slices/menuEnhancementSlice';
import PageHeader from '../common/PageHeader';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import SuggestionCard from './SuggestionCard';

type FilterType = 'all' | 'combo' | 'newItem';

const MenuEnhancement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { suggestions, stats, loading, error } = useSelector(
    (state: RootState) => state.menuEnhancement
  );
  const [activeFilter, setActiveFilter] = React.useState<FilterType>('all');

  useEffect(() => {
    // Fetch menu enhancement data with restaurant ID
    dispatch(fetchMenuEnhancementRequest('4bebbb8a-a2bf-4d53-b287-842c3c5be847'));
  }, [dispatch]);

  // Filter suggestions based on active filter
  const filteredSuggestions = React.useMemo(() => {
    if (activeFilter === 'all') return suggestions;
    if (activeFilter === 'combo') {
      return suggestions.filter(s => s.suggestion_type.toLowerCase().includes('combo'));
    }
    if (activeFilter === 'newItem') {
      return suggestions.filter(s => s.suggestion_type.toLowerCase().includes('new item'));
    }
    return suggestions;
  }, [suggestions, activeFilter]);

  if (loading) {
    return <LoadingState page="menu suggestions" />;
  }

  if (error) {
    return (
      <ErrorState 
        message="Failed to load menu suggestions" 
        error={error}
        onRetry={() => dispatch(fetchMenuEnhancementRequest('4bebbb8a-a2bf-4d53-b287-842c3c5be847'))}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black p-3">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <PageHeader 
          title="Menu Enhancement"
            userName="Delaney"
          showBackButton={true}
        />

        {/* Filter Tabs */}
        <div 
          className="rounded-xl p-2 mb-6 flex items-center gap-2"
          style={{
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
            background: '#21212180',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-6 cursor-pointer py-2 rounded-lg text-sm font-medium transition-all ${
              activeFilter === 'all'
                ? 'bg-[#EE4423] text-white'
                : 'bg-transparent text-gray-300 hover:text-white'
            }`}
          >
            All Suggestions ({stats.total})
          </button>
          <button
            onClick={() => setActiveFilter('combo')}
            className={`px-6 cursor-pointer py-2 rounded-lg text-sm font-medium transition-all ${
              activeFilter === 'combo'
                ? 'bg-[#EE4423] text-white'
                : 'bg-transparent text-gray-300 hover:text-white'
            }`}
          >
            Combo Deals ({stats.combos})
          </button>
          <button
            onClick={() => setActiveFilter('newItem')}
            className={`px-6 cursor-pointer py-2 rounded-lg text-sm font-medium transition-all ${
              activeFilter === 'newItem'
                ? 'bg-[#EE4423] text-white'
                : 'bg-transparent text-gray-300 hover:text-white'
            }`}
          >
            New Menu Items ({stats.newItems})
          </button>
        </div>

        {/* Suggestions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
          {filteredSuggestions.map((suggestion) => (
            <SuggestionCard key={suggestion.id} suggestion={suggestion} />
          ))}
        </div>

        {/* Empty State */}
        {filteredSuggestions.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              No {activeFilter === 'combo' ? 'combo' : activeFilter === 'newItem' ? 'new item' : ''} suggestions available
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuEnhancement;
